(() => {
  const rootPath = () => window.Shopify?.routes?.root || '/';
  const safePath = value => { try { const url = new URL(value, location.origin); return url.origin === location.origin ? url.pathname + url.search : '#'; } catch (_) { return '#'; } };
  const text = (tag, value, className) => { const node = document.createElement(tag); node.textContent = value || ''; if (className) node.className = className; return node; };

  window.Varellea.register('predictive-search', root => {
    const header = root.closest('.varellea-header');
    const input = root.querySelector('[data-search-input]');
    const results = root.querySelector('[data-search-results]');
    const status = root.querySelector('[data-search-status]');
    const hint = root.querySelector('[data-search-hint]');
    const clear = root.querySelector('[data-search-clear]');
    const all = root.querySelector('[data-search-all]');
    const close = root.querySelector('[data-search-close]');
    const controller = new AbortController();
    let fetchController, debounce, returnFocus, activeIndex = -1, options = [];
    const minimum = Number(root.dataset.minCharacters) || 2;
    const currency = root.dataset.currency || 'USD';
    const locale = root.dataset.locale || document.documentElement.lang || 'en';
    const money = value => { try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(value)); } catch (_) { return String(value); } };
    function setActive(index) {
      activeIndex = index;
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
      input.setAttribute('aria-activedescendant', index >= 0 ? options[index].id : '');
      options[index]?.scrollIntoView({ block: 'nearest' });
    }
    function reset() { fetchController?.abort(); clearTimeout(debounce); results.replaceChildren(); options = []; setActive(-1); input.setAttribute('aria-expanded', 'false'); all.hidden = true; status.textContent = ''; hint.hidden = false; }
    function dismiss() { if (root.open) root.close(); }
    function buildGroup(key, label, items) {
      if (!items?.length) return null;
      const group = document.createElement('div'); group.className = 'v-search-dialog__group'; group.setAttribute('role', 'group'); group.setAttribute('aria-label', label);
      group.append(text('h3', label));
      items.forEach(item => {
        const link = document.createElement('a'); link.className = 'v-search-dialog__result'; link.href = safePath(item.url); link.id = `${input.id}-option-${options.length}`; link.setAttribute('role', 'option'); link.setAttribute('aria-selected', 'false');
        if (key === 'products') {
          const imageUrl = item.featured_image?.url || item.image;
          if (imageUrl) { const image = document.createElement('img'); const url = new URL(imageUrl, location.origin); if (url.protocol === 'https:' || url.protocol === 'http:') { url.searchParams.set('width', '160'); image.src = url.toString(); image.alt = item.featured_image?.alt || ''; image.width = 72; image.height = 88; image.loading = 'lazy'; link.append(image); } }
        }
        const body = document.createElement('span'); body.className = 'v-search-dialog__result-body'; body.append(text('span', key === 'queries' ? item.text : item.title, 'v-search-dialog__result-title'));
        if (key === 'products') {
          const price = Number(item.price_min ?? item.price);
          const compare = Number(item.compare_at_price_min || 0);
          const row = document.createElement('span'); row.className = 'v-search-dialog__price'; row.append(text('span', money(price)));
          if (item.available && compare > price) { const crossed = text('s', money(compare)); row.append(crossed); row.append(text('span', root.dataset.saleLabel, 'v-search-dialog__sale')); }
          if (!item.available) row.append(text('span', root.dataset.soldOutLabel, 'v-search-dialog__sold-out'));
          body.append(row);
        }
        link.append(body); group.append(link); options.push(link);
      });
      return group;
    }
    async function fetchSuggestions(query) {
      fetchController?.abort();
      fetchController = new AbortController();
      results.replaceChildren(); options = []; setActive(-1); all.hidden = true; hint.hidden = true;
      status.textContent = root.dataset.loadingLabel;
      const loading = text('p', root.dataset.loadingLabel, 'v-search-dialog__message'); loading.setAttribute('role', 'status'); results.append(loading);
      const types = (root.dataset.resourceTypes || '').split(',').filter(Boolean).join(',') || 'product';
      const url = new URL(`${rootPath()}search/suggest.json`, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', types);
      url.searchParams.set('resources[limit]', root.dataset.resultLimit || '5');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', root.dataset.unavailable || 'last');
      try {
        const response = await fetch(url, { signal: fetchController.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (query !== input.value.trim()) return;
        const found = data.resources?.results || {};
        results.replaceChildren();
        const groups = [['queries', 'queriesLabel'], ['products', 'productsLabel'], ['collections', 'collectionsLabel'], ['articles', 'articlesLabel'], ['pages', 'pagesLabel']];
        for (const [key, label] of groups) { const group = buildGroup(key, root.dataset[label], found[key]); if (group) results.append(group); }
        if (!options.length) { results.append(text('p', root.dataset.emptyLabel, 'v-search-dialog__message')); status.textContent = root.dataset.emptyLabel; }
        else status.textContent = `${options.length} ${root.dataset.queriesLabel}`;
        input.setAttribute('aria-expanded', String(options.length > 0));
        const allUrl = new URL(all.href, location.origin); allUrl.searchParams.set('q', query); all.href = allUrl.toString(); all.hidden = false;
      } catch (error) {
        if (error.name === 'AbortError') return;
        results.replaceChildren(text('p', root.dataset.errorLabel, 'v-search-dialog__message'));
        status.textContent = root.dataset.errorLabel;
      }
    }
    function onInput() {
      clear.hidden = !input.value;
      clearTimeout(debounce);
      if (input.value.trim().length < minimum) { reset(); return; }
      debounce = setTimeout(() => fetchSuggestions(input.value.trim()), 220);
    }
    function onKeydown(event) {
      if (event.key === 'ArrowDown' && options.length) { event.preventDefault(); setActive((activeIndex + 1) % options.length); }
      else if (event.key === 'ArrowUp' && options.length) { event.preventDefault(); setActive(activeIndex <= 0 ? options.length - 1 : activeIndex - 1); }
      else if (event.key === 'Enter' && activeIndex >= 0) { event.preventDefault(); location.assign(options[activeIndex].href); }
      else if (event.key === 'Escape') { event.preventDefault(); dismiss(); }
    }
    function onOpen(event) { const link = event.target.closest('[data-search-open]'); if (!link || !header?.contains(link)) return; event.preventDefault(); returnFocus = link; if (!root.open) root.showModal(); input.focus(); }
    function onClose() { reset(); input.value = ''; clear.hidden = true; (returnFocus?.isConnected ? returnFocus : header?.querySelector('[data-search-open]'))?.focus(); }
    return {
      connect() {
        header?.addEventListener('click', onOpen, { signal: controller.signal });
        close?.addEventListener('click', dismiss, { signal: controller.signal });
        clear?.addEventListener('click', () => { input.value = ''; clear.hidden = true; reset(); input.focus(); }, { signal: controller.signal });
        input?.addEventListener('input', onInput, { signal: controller.signal });
        input?.addEventListener('keydown', onKeydown, { signal: controller.signal });
        root.addEventListener('close', onClose, { signal: controller.signal });
        root.addEventListener('click', event => { if (event.target === root) dismiss(); }, { signal: controller.signal });
      },
      disconnect() { controller.abort(); reset(); if (root.open) root.close(); }
    };
  });
})();
